import uib from 'angular-ui-bootstrap';
import fileSaver from 'file-saver';

/**
 * @ngdoc service
 * @name eventix.common.services.tableView.TableViewController
 * @description A public interface for controlling a `<table-view>`
 *
 * Controller is created by `table-view` and attaches to it and its parent scope.
 */
export default angular.module('eventix.common.directives.tableView', [uib])
    .factory('TableViewController', function(UIMessages, $q) {
        /**
         * Create a new TableViewController
         *
         * @constructor
         * @param {Scope} $scope Isolated scope of `<table-view>`
         * @param {String} ctrlName Name for interface object on scope
         */
        function Controller($scope, ctrlName = 'table') {
            $scope.$parent.$eval(`${ctrlName} = ctrl`, { ctrl: this });

            Object.defineProperty(this, 'scope', {
                value: $scope
            });
            this.reducers = [{
                name: 'index',
                priority: 10,
                fn: function(data, ctrl) {
                    _.forEach(data, (item, index) => {
                        if(ctrl.async)
                            index = index + ctrl.scope.perPage * ctrl.scope.page - 1;
                        Object.defineProperty(item, '$index', {
                            value: index,
                            writable: true,
                            configurable: true
                        });
                    });
                    return data;
                }
            }];
        }

        /**
         * @ngdoc property
         * @name eventix.common.services.tableView.TableViewController#async
         * @propertyOf eventix.common.services.tableView.TableViewController
         * @description True when data source is asynchronous
         */
        /**
         * @ngdoc property
         * @name eventix.common.services.tableView.TableViewController#loading
         * @propertyOf eventix.common.services.tableView.TableViewController
         * @description True when controller is loading data
         */

        /**
         * @ngdoc method
         * @name eventix.common.services.tableView.TableViewController#loadData
         * @methodOf eventix.common.services.tableView.TableViewController
         * @description
         * Load data from datasource and - if data is synchronous - runs it through the reducers
         *
         * Called on initialization, pagination and `table-view-refresh` events.
         *
         * Async data sources are called with the arguments `(offset, limit, sorting, filters)`
         *
         * ```
         * {
         *      offset: Number,
         *      limit: Number,
         *      sorting: {
         *          columnName: 'asc' || 'desc'
         *      },
         *      filters: {
         *          columnName: 'searchString'
         *      }
         * }
         * ```
         */
        Controller.prototype.loadData = function() {
            this.scope.page = this.scope.page || 0;
            this.scope.perPage = this.scope.perPage || 10;

            let start = Math.max(0, this.scope.page * this.scope.perPage);
            if(_.isArray(this.scope.rows)) {
                this.async = false;
                this.scope.totalRows = this.scope.rows.length;
                if(this.scope.page * this.scope.perPage > this.scope.totalRows) {
                    this.scope.page = 0;
                    start = 0;
                }
                let data = this.reduce(this.scope.rows);
                this.scope.totalRows = data.length;
                if(this.scope.page * this.scope.perPage > this.scope.totalRows) {
                    this.scope.page = 0;
                    start = 0;
                }
                this.scope.data = data.slice(start, start + this.scope.perPage);
            } else if(_.isFunction(this.scope.rows)) {
                this.async = true;
                this.scope.loading = true;
                let sorters = _.filter(this.reducers, r => r.name.indexOf('sort') === 0);
                let filters = _.filter(this.reducers, r => r.name.indexOf('filter') === 0);
                let orderBy = _.map(sorters, r => r.serialize());
                orderBy = _.fromPairs(_.compact(orderBy));
                let filterOn = _.map(filters, r => r.serialize());
                filterOn = _.fromPairs(_.compact(filterOn));

                this.scope.rows(start, this.scope.perPage, orderBy, filterOn)
                    .then(d => {
                        this.scope.data = this.reduce(d).slice(0, this.scope.perPage);
                        this.scope.loading = false;
                    }, err => {
                        this.scope.loading = false;
                        return $q.reject(err);
                    });
            }

            this.scope.pages = _.range(Math.ceil(this.scope.totalRows / this.scope.perPage));
        };

        /**
         * @ngdoc method
         * @name eventix.common.services.tableView.TableViewController#addReducer
         * @methodOf eventix.common.services.tableView.TableViewController
         * @description
         * Add a reducer to manipulate dataset
         *
         * @param {Object} def Reducer definition, should include the properties `name,fn,serialize,[priority]`
         *
         *  - Sorting reducers should have names starting with `sort_`, filters with `filter_`.
         *  - The `fn` is called with `fn({Array}, {TableViewController})` and should return `{Array}`
         *  - Serialize is called for sorting and filter reducers and is expected to return
         *    something an array of `[columnName, direction || filter]`. This output is
         *    sent an async datasource for serverside processing.
         */
        Controller.prototype.addReducer = function(def) {
            let existing = _.find(this.reducers, {name: def.name});
            if(existing) {
                existing.fn = def.fn;
                existing.priority = def.priority;
            } else {
                if(_.isNil(def.priority))
                    def.priority = 10;
                this.reducers.push(def);
            }
            this.reducers = _.sortBy(this.reducers, 'priority');
        };

        /**
         * @ngdoc method
         * @name eventix.common.services.tableView.TableViewController#reduce
         * @methodOf eventix.common.services.tableView.TableViewController
         * @description
         * Run reducers over given dataset
         *
         * @param {Object[]} data Dataset
         * @returns {Object[]} Sorted, filtered dataset
         */
        Controller.prototype.reduce = function(data) {
            return _.reduce(this.reducers, (data, reducer) => {
                if(this.async && /^(sort|filter)/.test(reducer.name))
                    return data;
                return reducer.fn(data, this);
            }, data);
        };

        /**
         * @ngdoc method
         * @name eventix.common.services.tableView.TableViewController#getData
         * @methodOf eventix.common.services.tableView.TableViewController
         * @description
         * Get complete dataset, parsed through reducers
         *
         * @returns {Array|Promise} Sorted, filtered dataset or the promise thereof depending on data source
         */
        Controller.prototype.getData = function() {
            if(!this.async)
                return this.reduce(this.scope.rows);

            let sorters = _.filter(this.reducers, r => r.name.indexOf('sort') === 0);
            let filters = _.filter(this.reducers, r => r.name.indexOf('filter') === 0);
            let orderBy = _.map(sorters, r => r.serialize());
            orderBy = _.fromPairs(_.compact(orderBy));
            let filterOn = _.map(filters, r => r.serialize());
            filterOn = _.fromPairs(_.compact(filterOn));
            return this.scope.rows(0, this.scope.totalRows, orderBy, filterOn);
        };

        return Controller;
    })
    .directive('tableView', function() {
        return {
            priority: 100,
            compile: function(tElement, tAttrs) {
                var table = angular.element(`<div class="table-container" scroll-hint>
    <table ng-class="class">
        <thead> </thead>
        <tbody> </tbody>
        <tfoot> </tfoot>
    </table>
</div>
<ul class="pagination noselect" ng-hide="!pages || pages.length < 2">
    <li class="meta form-inline" style="padding-right: 10px">
        Per page <select class="form-control" ng-options="i for i in pageSizes" ng-model="perPage"></select>
    </li>
    <li class="meta"><a ng-click="goto(0)">Start</a></li>
    <li class="meta"><a ng-click="goto('back')">Prev</a></li>
    <li ng-repeat="p in pages | filter:surround" ng-class="{active: page == p}"><a ng-click="goto(p)">{{p+1}}</a></li>
    <li class="meta"><a ng-click="goto('next')">Next</a></li>
    <li class="meta"><a ng-click="goto(pages.length - 1)">End</a></li>
    <li class="meta" ng-hide="tv.ctrl.async"><a ng-click="showAll()">Show all</a></li>
    <li class="meta" style="line-height: 22pt; margin: 15px;">{{ totalRows }} Items</li>
</ul>
<div class="bottom-right pull-right"></div>`);

                let slots = {
                    header: tElement.find('table-header'),
                    rows: tElement.find('table-rows'),
                    footer: tElement.find('table-footer'),
                    bottomRight: tElement.find('table-bottom-right')
                };
                /* Header row template */
                if (slots.header.length) {
                    let tr = document.createElement('tr');
                    copyAttributes(slots.header.get(0), tr);
                    slots.header.find('cell').each((i, cell) => {
                        let th = document.createElement('th');
                        th.innerHTML = cell.innerHTML;
                        copyAttributes(cell, th);
                        modifyDropdown(th, i > 0);
                        tr.appendChild(th);
                    });
                    table.find('thead').append(tr);
                }
                /* Bottom-right template */
                if(slots.bottomRight.length) {
                    let div = document.createElement('div');
                    copyAttributes(slots.bottomRight.get(0), div);
                    div.innerHTML = slots.bottomRight.html();
                    table.filter('.bottom-right').append(div);
                }
                /* Footer row template */
                if (slots.footer.length) {
                    let tr = document.createElement('tr');
                    copyAttributes(slots.footer.get(0), tr);
                    slots.footer.find('cell').each((i, cell) => {
                        let th = document.createElement('th');
                        th.innerHTML = cell.innerHTML;
                        copyAttributes(cell, th);
                        modifyDropdown(th, i > 0);
                        tr.appendChild(th);
                    });
                    table.find('tfoot').append(tr);
                }
                /* Row template for the data */
                if (slots.rows) {
                    let tr = document.createElement('tr');
                    copyAttributes(slots.rows.get(0), tr);
                    let itemName = tAttrs.as || 'item';
                    tr.setAttribute('ng-repeat', itemName + ' in data track by $index');
                    slots.rows.find('cell').each((i, cell) => {
                        let td = document.createElement('td');
                        td.innerHTML = cell.innerHTML;
                        copyAttributes(cell, td);
                        modifyDropdown(td, i > 0);
                        tr.appendChild(td);
                    });
                    table.find('tbody').append(tr);
                }

                function copyAttributes(src, dst) {
                    if(!src.hasAttributes())
                        return dst;
                    for(let i = 0; i < src.attributes.length; i++)
                        dst.setAttribute(src.attributes[i].name, src.attributes[i].value);
                    return dst;
                }

                function modifyDropdown(cell, right) {
                    cell = angular.element(cell);
                    let dropdowns = cell.find('[uib-dropdown]');
                    if(dropdowns && dropdowns.length)
                        dropdowns.attr('dropdown-append-to-body', true);
                    if(right)
                        cell.find('[uib-dropdown-menu]').addClass('dropdown-menu-right');
                }
                tElement.html(table);
            }
        };
    })
    .directive('scrollHint', function($interval) {
        function ScrollHint(scope, el, attr) {
            if(el.parents('.pin-left,.pin-right').length)
                return;
            var interval = $interval(checkOverflow, 250);
            el.on('scroll', function() {
                el.removeClass('h-overflow');
                $interval.cancel(interval);
            });
            function checkOverflow() {
                if(el.prop('scrollWidth') > el.prop('clientWidth'))
                    el.addClass('h-overflow');
                else
                    el.removeClass('h-overflow');
            }
        }
        return {
            restrict: 'AC',
            link: ScrollHint
        };
    })
    .directive('tableView', function($compile, TableViewController, $parse) {
        /**
         * @ngdoc directive
         * @name eventix.common.services.tableView.tableView
         * @restrict element
         * @description
         * Root directive for a table view
         *
         * Controls settings like pagination, accepts data and reflects current state.
         * Creates a control object under current scope according to TableViewController
         *
         * @example
         * ```
         * <table-view paginate="true" per-page="10" page="3" rows="list" as="item" table-controller-as="vm.table" deep-watch="false" pin="right">
         *     <table-header>
         *         <cell>Name</cell>
         *         <cell>Email</cell>
         *     </table-header>
         *     <table-rows>
         *         <cell>{{ item.name }}</cell>
         *         <cell>{{ item.email }}</cell>
         *     </table-rows>
         *     <table-footer>
         *         <cell colspan="2">Powered by TableView</cell>
         *     </table-footer>
         * </table-view>
         * ```
         * @param {Number} [perPage=20] Rows per page
         * @param {Number} [tatalRows] Total row count (required for async data)
         * @param {Number} [page=2] Current page
         * @param {Array|Function} rows Data array or a function that returns a promise that resolves with an array
         * @param {String} [as=item] Name for item in rows
         * @param {String} [tableControllerAs=table] Name of table controller object on parent scope
         * @param {Boolean} [deepWatch=true] Wheter to deepwatch changes, useful for large tables to disable deepwatch for performance
         * @param {Boolean} [pin=none|right|left] Wheter to pin a colomn, right is default
         */
        return {
            restrict: 'E',
            scope: true,
            transclude: true,
            controllerAs: 'tv',
            controller: /*@ngInject*/ function($scope, $element, $attrs, $transclude, $interval) {
                const ctrl = new TableViewController($scope, $attrs.tableControllerAs);
                this.ctrl = ctrl;
                var throttledLoader = NODE_ENV === 'testing' ? () => ctrl.loadData() : _.debounce(() => {
                    ctrl.loadData();
                    $scope.$applyAsync();
                }, 500);

                $scope.perPage = 10;
                $scope.pageSizes = [10,25,50];
                $scope.page = 0;

                /* set up bindings */
                _.forEach(['class'], attr => {
                    $scope[attr] = '';
                    $attrs.$observe(attr, value => $scope[attr] = value);
                });
                // NEON theme hack
                $element.removeClass('table');

                $scope.deepWatch = _.get($attrs, 'deepWatch', true);

                // Default pin right column
                let pin = _.get($attrs, 'pin', 'right');
                if(pin === 'right' || pin === 'left') {
                    $element.addClass('pin-' + pin);
                    let container = null;
                    $interval(function() {
                        if(!container)
                            container = $element.find('.table-container');
                        if(container.prop('scrollWidth') > container.prop('clientWidth'))
                            $element.removeClass('pin-remove-background');
                        else
                            $element.addClass('pin-remove-background');
                    }, 500);
                }


                // Set-up bi-directional deep-sync between selected attributes
                _.forEach(['page', 'rows', 'perPage', 'totalRows', 'loading'], attr => {
                    let fn = $parse($attrs[attr]);
                    $scope.$watch(() => fn($scope.$parent), value => {
                        if(!_.isNil(value))
                            $scope[attr] = value;
                    },$scope.deepWatch);

                    $scope.$watch(attr, value => {
                        if(fn.assign && !_.isNil(value))
                            fn.assign($scope, value);
                        if(attr === 'totalRows') {
                            if(_.isNumber(value))
                                $scope.pages = _.range(Math.ceil(value / $scope.perPage));
                        } else if(attr === 'loading') {
                            if(value) {
                                $scope.data = _.times($scope.perPage, _.constant({}));
                                $element.attr('loading', value);
                            } else if(value === false) {
                                let deregisterLoading = $scope.$watch('data', data => {
                                    if(!_.isEmpty(data) && !_.isEmpty(data[0]))
                                        deregisterLoading();

                                    $element.attr('loading', null);
                                });
                            }
                        } else
                            throttledLoader();
                    }, $scope.deepWatch);
                });

                $scope.$on('table-view-refresh', () => {$scope.page = 0; return throttledLoader();});
                $scope.$on('table-view-notify-enabled-sort', (event, name) => {
                    $scope.$broadcast('table-view-enabled-sort', name);
                });

                let showSurrounding = 2;
                // Filter to show max 5 page links in pagination
                $scope.surround = function(p) {
                    if(!$scope.pages) return true;
                    if($scope.pages.length < showSurrounding * 2 + 1)
                        return true;
                    if($scope.page <= showSurrounding)
                        return p < showSurrounding * 2 + 1;
                    if($scope.page >= $scope.pages.length - 1 - showSurrounding * 2)
                        return p >= $scope.pages.length - 1 - showSurrounding * 2;
                    return p >= $scope.page - showSurrounding && p <= $scope.page + showSurrounding;
                };

                $scope.showAll = function() {
                    $scope.perPage = $scope.totalRows;
                    ctrl.scope.$emit('table-view-refresh');
                };

                // Goto page in pagination
                $scope.goto = function(p) {
                    if(_.isString(p))
                        $scope.page = p === 'next' ? $scope.page + 1 : $scope.page - 1;
                    else
                        $scope.page = p;
                    $scope.page = Math.max(0, Math.min($scope.pages.length - 1, $scope.page));
                };

                $transclude($scope, function(clone) { $element.append(clone); });
            }
        };
    })
    .directive('tableSortable', function($log) {
        /**
         * @ngdoc directive
         * @name eventix.common.services.tableView.tableSortable
         * @restrict element
         * @description
         * Adds a sorting reducer
         *
         * @example
         * ```
         * <table-sortable
         *     property="address.city"
         *     directions="both">
         * </table-sortable>
         * ```
         *
         * @param {String} [property] Property to filter (required when `custom-fn` is not given)
         * @param {String} [directions] Allowed sorting directions `(asc,desc,both)`
         * @param {String} [direction] (asc,desc)
         * @param {Boolean} [enabled] Whether the sort is enabled
         * @param {Number} [priority=10] Reducer priority
         * @param {Boolean} [allowMultiple=false] Allow multiple sorting directions
         * @param {Function} [customFn] Custom sorting function that fulfils interface `{Array} customFn(collection{Array})`
         */
        return {
            restrict: 'E',
            require: '^^?tableView',
            template: '<a ng-click="toggleSort()" class="btn btn-xs btn-default"><i class="fa" ng-class="findIcon()"></i></a>',
            scope: {
                property: '@?',
                enabled: '=?',
                directions: '@?',
                direction: '@?',
                priority: '@?',
                customFn: '&?'
            },
            link: function(scope, el, attr, vm) {
                if(!vm || !vm.ctrl)
                    return;

                _.defaults(scope, {
                    directions: 'both',
                    direction: 'asc',
                    priority: 10
                });
                const def = {
                    name: 'sort_' + (scope.property || generateRandomName(8)),
                    priority: scope.priority,
                    fn: sort,
                    serialize: serialize
                };
                vm.ctrl.addReducer(def);

                scope.$watch('direction', notify);
                scope.$watch('property', notify);
                scope.$watch('priority', notify);
                scope.$watch('customFn', notify);

                /* Notify other sorting functions if we're enabled */
                scope.$watch('enabled', n => {
                    if(n) vm.ctrl.scope.$emit('table-view-notify-enabled-sort', def.name);
                    notify();
                });

                /* If allowed directions are changed, update sorting direction to an allowed one */
                scope.$watch('directions', (dirs) => {
                    if(dirs !== 'both' && scope.direction !== dirs) {
                        scope.direction = dirs;
                        if(scope.enabled)
                            notify();
                    }
                });

                /* Disable this sorting if another is enabled */
                vm.ctrl.scope.$on('table-view-enabled-sort', (ev, name) => {
                    if(name !== def.name && scope.enabled)
                        scope.enabled = false;
                });

                scope.toggleSort = function() {
                    if(!scope.enabled) {
                        if(vm.ctrl.async && !scope.property)
                            return $log('Can\'t enable a custom sort on async data source');
                        if(scope.directions === 'both' || scope.directions === 'asc')
                            scope.direction = 'asc';
                        else
                            scope.direction = 'desc';
                        return scope.enabled = true;
                    } else if(scope.direction === 'asc' && /both|desc/.test(scope.directions))
                        return scope.direction = 'desc';
                    return scope.enabled = false;
                };

                scope.findIcon = function() {
                    if(!scope.enabled)
                        return 'fa-sort';
                    if(scope.direction === 'asc')
                        return 'fa-sort-up';
                    return 'fa-sort-down';
                };

                function sort(data) {
                    if(!scope.enabled)
                        return data;
                    if(_.isFunction(scope.customFn) && !vm.ctrl.async)
                        return scope.customFn({collection: data, direction: scope.direction});
                    if(_.isString(scope.property))
                        return _.orderBy(data, scope.property, [scope.direction]);
                    return data;
                }

                function notify() {
                    vm.ctrl.scope.$emit('table-view-refresh', def.name);
                }

                function serialize() {
                    if(!scope.enabled) return null;
                    return [scope.property, scope.direction];
                }
            }
        };
        function generateRandomName(length) {
            return String.fromCharCode.apply(null, _.times(length, () => _.random(97,122)));
        }
    })
    .directive('tableFilter', function($document, uibTypeaheadParser, $log, Fuse, $uibPosition) {
        /**
         * @ngdoc directive
         * @name eventix.common.services.tableView.tableFilter
         * @restrict element
         * @description
         * Adds a filter reducer
         *
         * @example
         * ```
         * <table-filter
         *     property="address.city"
         *     type="text-search"
         *     fuzzy="true">
         * </table-filter>
         * ```
         *
         * @param {String} type Filter type, supports types
         *    [text-search, fixed-enum, dynamic-enum]
         * @param {String} [property] Property to filter
         *    (required when `custom-fn` or `fuzzy` is not given)
         * @param {Expression} [options] An `ngOptions` type of expression
         *    `modelValue (as label) for item in collection`
         *    (required when `type === fixed-enum`)
         * @param {Number} [priority=10] Reducer priority
         * @param {Object} [fuzzy] Fuzzy search matches `Hugu Bose`
         *    with `Hugo Boss`. It uses the Fuse.js library and expects an options object
         * @param {Function} [customFn] Custom filter function that
         *    receives `(item, key, collection)`
         */
        return {
            restrict: 'E',
            require: '^^?tableView',
            template: `<a class="btn btn-xs table-view-filter-trigger" ng-click="toggle($event)" ng-class=" enabled ? 'btn-warning' : 'btn-default' ">
    <i class="fa fa-search"></i>
</a>
<div class="table-view-filter-box ng-hide" ng-click="prevent($event)">
    <div class="input-group input-group-sm">
        <div class="input-group-addon">
            <i class="fa fa-search" ></i>
        </div>
        <select class="form-control"
            ng-if="type!='text-search'"
            ng-change="close()"
            ng-blur="close()"
            ng-options="o.model as o.label for o in options"
            ng-model="filter.search">
            <option value=""></option>
        </select>
        <input type="text" class="form-control"
            ng-blur="close()"
            ng-if="type=='text-search'"
            ng-model="filter.search" />
    </div>
</div>`,
            scope: {
                property: '@?',
                priority: '@?',
                customFn: '&?',
                fuzzy: '=?',
                type: '@?'
            },
            link: function(scope, el, attr, vm) {
                if(!vm || !vm.ctrl)
                    return;

                scope.filter = {
                    search: ''
                };
                _.defaults(scope, {
                    type: 'text-search',
                    fuzzy: null,
                    enabled: false,
                    priority: 10
                });
                const def = {
                    name: 'filter_' + (scope.property || generateRandomName(8)),
                    priority: scope.priority,
                    fn: filter,
                    serialize: serialize
                };
                vm.ctrl.addReducer(def);
                var optionsParser;

                /* Generate dropdown options for fixed enum */
                if(scope.type === 'fixed-enum') {
                    if(!attr.options) {
                        $log.error('fixed_enum without options given');
                        return;
                    }
                    // parse options string
                    optionsParser = uibTypeaheadParser.parse(attr.options);
                    scope.$watch(() => {
                        // detect source array changes
                        return optionsParser.source(scope.$parent);
                    }, options => {
                        if(_.isNil(options)) return;
                        // build new array of select options
                        scope.options = buildOptionsList(options);
                    });
                }
                /* Generate dropdown options for dynamic enum */
                if(scope.type === 'dynamic-enum') {
                    if(vm.ctrl.async) {
                        $log.error('dynamic-enum is not supported for async data source');
                        return;
                    }
                    vm.ctrl.scope.$watch('rows', data => {
                        var options = _.map(data, scope.property);
                        options = _.uniq(_.compact(options));
                        scope.options = _.map(options, item => {
                            return {
                                model: item,
                                label: item
                            };
                        });
                    });
                }
                scope.$watch('enabled', notify);
                scope.$watch('property', notify);
                scope.$watch('priority', notify);
                scope.$watch('customFn', notify);

                scope.$watch('filter.search', s => {
                    scope.enabled = s && s.length > 2;
                    notify();
                });

                scope.prevent = (e) => e.stopPropagation();
                scope.toggle = (e) => scope.filter.open ? close(e) : open(e);
                /* Close on [ tab, esc, enter ] */
                scope.shouldClose = shouldClose;

                function shouldClose(ev) {
                    [13, 27].indexOf(ev.which) >= 0 ?  close() : angular.noop;
                }

                function close() {
                    el.removeClass('open');
                    angular.element($document).off('click', close);
                    angular.element($document).off('keydown', scope.shouldClose);
                    el.find('.table-view-filter-box').addClass('ng-hide');
                    scope.filter.open = false;
                }

                function open(ev) {
                    if(ev)
                        ev.stopPropagation();
                    scope.filter.open = true;
                    let box = el.find('.table-view-filter-box');
                    box.removeClass('ng-hide');
                    el.addClass('open');
                    el.find('input,select').eq(0).focus();

                    let pos = $uibPosition.positionElements(
                        el.find('.table-view-filter-trigger'),
                        box,
                        'bottom'
                    );
                    box.css({
                        position: 'absolute',
                        top: pos.top + 5,
                        left: pos.left
                    });
                    $document.on('click', close);
                    $document.on('keydown', shouldClose);
                }

                function filter(data) {
                    if(!scope.enabled)
                        return data;
                    if(_.isFunction(scope.customFn) && !vm.ctrl.async)
                        return scope.customFn({ collection: data });
                    if(!_.isNil(scope.fuzzy)) {
                        var opts = scope.fuzzy;
                        if(!_.isObject(opts)) {
                            opts = {
                                keys: [scope.property],
                                threshold: 0.8,
                                shouldSort: true
                            };
                        }
                        var fuse = new Fuse(data, opts);
                        return fuse.search(scope.filter.search);
                    }
                    if(_.isString(scope.property)) {
                        let matcher = scope.filter.search;
                        if(scope.type === 'text-search') {
                            matcher = matcher.replace(/e+/i, '[eèéêẽë]+')
                                .replace(/a+/i, '[aàáâãä]+')
                                .replace(/i+/i, '[iìíîĩï]+')
                                .replace(/o+/i, '[oòóôõö]+')
                                .replace(/u+/i, '[uùúûũü]+');
                            matcher = new RegExp(matcher, 'i');
                        } else
                            matcher = new RegExp('^' + matcher + '$', 'i');
                        return _.filter(data, item => {
                            return matcher.test(_.get(item, scope.property, '').toString());
                        });
                    }
                    return data;
                }

                function buildOptionsList(options) {
                    return _.map(options, option => {
                        var local = {};
                        local[optionsParser.itemName] = option;
                        return {
                            model: optionsParser.modelMapper(local),
                            label: optionsParser.viewMapper(local)
                        };
                    });
                }

                function notify() {
                    vm.ctrl.scope.$emit('table-view-refresh', def.name);
                }

                function serialize() {
                    if(!scope.enabled) return null;
                    return [scope.property, scope.filter.search];
                }
            }
        };
        function generateRandomName(length) {
            return String.fromCharCode.apply(null, _.times(length, () => _.random(97,122)));
        }
    })
    .directive('tableExport', function($filter, UIMessages, CSVExport) {
        /**
         * @ngdoc directive
         * @name eventix.common.services.tableView.tableExport
         * @restrict element
         * @description
         * Export table to CSV
         *
         * Supports nested objects by serializing them to something akin to YAML
         *
         * @example
         * ```
         * <table-export columns='{ "created_at": "Datum", "firstName" : "Voornaam", "tech_data.decoded.facebookId": "FacebookID" }'></table-export>
         * <table-export columns='["guid", "firstName"]'></table-export>
         * <table-export table='*tablename*'></table-export>
         * <table-export name='file_to_export.csv'></table-export>
         *
         * // Select guid and name
         * <table-export columns="['guid','name']"></table-export>
         *
         * // Select guid and name, giving the column headers a custom name
         * <table-export columns="{'guid': 'OrderID','name': 'Voornaam'}"></table-export>
         *
         * // Select guid and all products from all tickets
         * <table-export columns="['guid','tickets.*.products']"></table-export>
         *
         * // Select guid and all product names from products where VAT = 21%, across all tickets
         * <table-export columns="['guid','tickets.*.products[vat=21].name']"></table-export>
         *
         * // Select guid and all product names from products where name contains "Access for", across all tickets
         * <table-export columns="['guid','tickets.*.products[name*=Access for].name']"></table-export>
         * ```
         *
         * @param {String} [name] Filename for the export
         * @param {TableViewController} [table] Required if this element is not a child element of `<table-view>`.
         * @param {String} [placeholder] Label for the button
         * @param {String[]|Object} [columns] Which object keys to export as columns in CSV. You can provide "deep"
         * properties such as `date.location.name`.
         *
         * If you provide an array of property names, a table header is generated by running properties through `humanize`
         * filter. If you provide an object, the keys should be property names and the values their corresponding column
         * headers.
         */
        return {
            restrict: 'E',
            scope: {
                name: '@?',
                table: '=?',
                columns: '=?',
                btnClass: '@?',
                placeholder: '@?'
            },
            transclude: true,
            template: `<a ng-class="btnClass" target="_blank" ng-click="export()">
    <span ng-transclude><i class="fa fa-download"></i></span>
</a>`,
            require: '^^?tableView',
            link: function(scope, el, attr, vm) {
                if((!vm || !vm.ctrl) && !scope.table)
                    return;
                else if(vm && vm.ctrl)
                    scope.table = vm.ctrl;
                else if(scope.table)
                    vm = { ctrl: scope.table };
                if(!scope.btnClass || !scope.btnClass.length)
                    scope.btnClass = 'btn btn-default btn-xs';

                var humanize = $filter('humanize');

                scope.export = function() {
                    var data = vm.ctrl.getData();
                    if(_.isArray(data)) {
                        let csv = CSVExport.convert(data, scope.columns, scope.name);
                        if(csv instanceof Error)
                            UIMessages.push('common.notice.notFound');
                    } else {
                        data.then(() => CSVExport.convert(data, scope.columns, scope.name))
                            .catch(error => UIMessages.push(error.error_description || error.message || 'common.notice.unknownError'));
                    }
                };
            }
        };
    }).name;
